import { Button, Card, CardBody, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Spinner, TabContent, TabPane } from "reactstrap";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import sidebar from "../../utils/sidebar";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import {
  IoAttachOutline, IoBriefcaseOutline, IoCallOutline, IoCheckmarkCircleOutline, IoCopyOutline,
  IoCreateOutline, IoFlagOutline, IoMailOutline, IoOpenOutline, IoPersonOutline, IoReaderOutline,
  IoSwapVerticalOutline, IoTimeOutline, IoTrashOutline
} from "react-icons/io5";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { del, get, post } from "../../../../utils/helpers/api";
import OverviewTab from "./Tabs/Overview";
import Timeline from "../../components/Timeline";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../context/settings";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import { toast } from "react-toastify";
import {
  callStatusLabel, callTypeLabel, formatMinutes, personName, printCallStatus, printCallType
} from "../../utils/helpers";
import {
  Avatar, Fact, RecordLayout, RecordMain, RecordNav, RecordRail, RecordTabs, StatStrip, useScrollSpy
} from "../../components/Record";

const STATUS_TINTS = {
  scheduled: { background: '#e6f4ff', color: '#1d7afc' },
  completed: { background: '#e9f9f0', color: '#22a06b' },
  cancelled: { background: '#ffe6eb', color: '#e2214b' }
};

function ViewCallPage() {
  const { t } = useTranslation();
  const { formatDateTime } = useSettings();
  const NAV_SECTIONS = [
    { id: 'call-informations-card', label: t('crm.callInformationsSection'), icon: <IoCallOutline size={14} /> },
    { id: 'call-purpose-card', label: t('crm.purpose'), icon: <IoFlagOutline size={14} /> },
    { id: 'notes-card', label: t('common.notes'), icon: <IoReaderOutline size={14} /> },
    { id: 'attachments-card', label: t('attachments.title'), icon: <IoAttachOutline size={14} /> }
  ];
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [callData, setCallData] = useState(state?.callData || {});
  const [callLoading, setCallLoading] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { activeSection, scrollToSection } = useScrollSpy(
    NAV_SECTIONS.map(section => section.id),
    scrollContainerRef,
    activeTab === 'overview',
    callData?.id
  );

  useEffect(() => {
    if (id) {
      getCall();
    }
  }, [id]);

  const getCall = async () => {
    setCallLoading(true);
    const data = await get('/crm/calls/view/' + id);
    if (data?.call) {
      setCallData(data.call);
      setCallLoading(false);
    }
  };

  const cloneCall = async () => {
    const data = await post('/crm/calls/clone/' + id);
    if (data?.call) {
      navigate('/crm/calls/edit/' + data.call.id, { state: { callData: data.call } });
    }
  };

  const deleteCall = async () => {
    const data = await del('/crm/calls/delete/' + id);
    if (data?.success) {
      toast.success(data.message || t('crm.callDeleted'));
      navigate('/crm/calls');
    }
  };

  /* Only the status is sent, the update endpoint leaves the fields it does not receive alone. */
  const markCompleted = async () => {
    const data = await post('/crm/calls/update/' + id, { status: 'completed' });
    if (data?.success) {
      setCallData(prev => ({ ...prev, status: 'completed' }));
    }
  };

  const subject = callData?.subject || t('crm.callDetails');
  const customer = callData?.customer;
  const customerName = customer?.company_name || personName(customer);
  const relatedTo = callData?.call_for === 'lead' && callData?.lead_id
    ? `/crm/leads/view/${callData.lead_id}`
    : customer?.id ? `/crm/customers/view/${customer.id}` : null;
  const statusTint = STATUS_TINTS[callData?.status] ?? { background: '#f4f4f6', color: '#8a8a90' };

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          title={subject}
          onBack={() => navigate('/crm/calls')}
        >
          {callData?.status !== 'completed' && (
            <Button size="sm" color="primary" className="d-flex align-items-center gap-1" onClick={markCompleted}>
              <IoCheckmarkCircleOutline size={14} />
              {t('crm.markCompleted')}
            </Button>
          )}
          <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(prev => !prev)} direction="down" size="sm">
            <DropdownToggle outline color="secondary">
              <PiDotsThreeOutlineVerticalFill />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => navigate("/crm/calls/edit/" + id, { state: { callData } })}>
                <IoCreateOutline size={15} /> {t('common.edit')}
              </DropdownItem>
              <DropdownItem onClick={cloneCall}>
                <IoCopyOutline size={15} /> {t('common.clone')}
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem className="text-danger" onClick={() => setDeleteOpen(true)}>
                <IoTrashOutline size={15} /> {t('common.delete')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </PageSubHeader>

        <div className="subheader-page-content scrollable" ref={scrollContainerRef}>
          {callLoading ?
            <Spinner color="primary" className="d-block mx-auto my-4" />
          :
            <RecordLayout>
              <RecordRail>
                <Card>
                  <CardBody className="text-center pb-3">
                    <Avatar fallback={<IoCallOutline size={26} />} />

                    <h6 className="fw-bold mb-2 mt-3">{subject}</h6>
                    <div className="d-flex justify-content-center gap-1 flex-wrap">
                      {printCallStatus(callData?.status)}
                      {printCallType(callData?.call_type)}
                    </div>

                    <div className="d-flex justify-content-center gap-2 mt-3">
                      <Button
                        size="sm"
                        outline
                        color="secondary"
                        disabled={!customer?.primary_phone}
                        onClick={() => { window.location.href = `tel:${customer.primary_phone}`; }}
                      >
                        <IoCallOutline size={14} />
                      </Button>
                      <Button
                        size="sm"
                        outline
                        color="secondary"
                        disabled={!customer?.primary_email}
                        onClick={() => { window.location.href = `mailto:${customer.primary_email}`; }}
                      >
                        <IoMailOutline size={14} />
                      </Button>
                      <Button
                        size="sm"
                        outline
                        color="secondary"
                        disabled={!relatedTo}
                        onClick={() => navigate(relatedTo)}
                      >
                        <IoOpenOutline size={14} />
                      </Button>
                    </div>
                  </CardBody>

                  <hr className="my-0" />

                  <CardBody className="d-flex flex-column gap-2">
                    <Fact icon={<IoPersonOutline size={14} />} value={customerName} />
                    <Fact icon={<IoBriefcaseOutline size={14} />} value={personName(callData?.employee)} />
                    <Fact
                      icon={<IoTimeOutline size={14} />}
                      value={callData?.call_start_time ? formatDateTime(callData.call_start_time) : null}
                    />
                    <Fact icon={<IoFlagOutline size={14} />} value={callData?.call_purpose} />
                    <Fact icon={<IoCheckmarkCircleOutline size={14} />} value={callData?.call_result} />
                  </CardBody>
                </Card>

                {activeTab === 'overview' && (
                  <RecordNav sections={NAV_SECTIONS} activeId={activeSection} onSelect={scrollToSection} />
                )}
              </RecordRail>

              <RecordMain>
                <StatStrip
                  tiles={[
                    {
                      icon: <IoCheckmarkCircleOutline size={18} />,
                      background: statusTint.background,
                      color: statusTint.color,
                      value: callStatusLabel(callData?.status) || '-',
                      label: t('common.status')
                    },
                    {
                      icon: <IoSwapVerticalOutline size={18} />,
                      background: '#f0eeff',
                      color: '#6C63FF',
                      value: callTypeLabel(callData?.call_type) || '-',
                      label: t('crm.callType')
                    },
                    {
                      icon: <IoTimeOutline size={18} />,
                      background: '#e6f4ff',
                      color: '#1d7afc',
                      value: callData?.call_start_time ? formatDateTime(callData.call_start_time) : '-',
                      label: t('crm.callStartTime')
                    },
                    {
                      icon: <IoCallOutline size={18} />,
                      background: '#fff3e6',
                      color: '#fd7e14',
                      value: formatMinutes(callData?.call_duration) || '-',
                      label: t('common.duration')
                    }
                  ]}
                />

                <RecordTabs
                  tabs={[
                    { id: 'overview', label: t('crm.overview') },
                    { id: 'timeline', label: t('crm.timeline') }
                  ]}
                  activeTab={activeTab}
                  onChange={setActiveTab}
                />
                <TabContent activeTab={activeTab} className="border-0 p-0">
                  <TabPane tabId="overview">
                    <OverviewTab callData={callData} />
                  </TabPane>
                  <TabPane tabId="timeline">
                    <Timeline subjectType="call" subjectId={id} />
                  </TabPane>
                </TabContent>
              </RecordMain>
            </RecordLayout>
          }
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteOpen}
        toggle={() => setDeleteOpen(false)}
        onConfirm={deleteCall}
        title={t('crm.deleteCall')}
        message={t('crm.deleteCallConfirm')}
      />
    </SidebarLayout>
  );
}

export default ViewCallPage;
